import { injectable } from 'tsyringe';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';

export interface TenantUser {
    id: string;
    email: string;
    displayName: string;
    firstName?: string;
    lastName?: string;
    status: string; // 'active' | 'inactive'
    roles: string[];
    createdAt?: string;
}

export interface InviteTenantUserInput {
    email: string;
    firstName?: string;
    lastName?: string;
    roles?: string[];
}

async function readError(res: Response, fallback: string): Promise<string> {
    try {
        const body = (await res.json()) as { error?: string };
        if (body.error) return body.error;
    } catch { /* keep fallback */ }
    return fallback;
}

/**
 * Operator-side management of a tenant's users (the tenant drill-down Users tab).
 * Hits the operator-gated auth surface `/api/operator/tenants/:id/users`, where
 * the path tenant is the authority. All calls go through the BFF.
 */
@injectable()
export class OperatorTenantUsersService {
    private base(tenantId: string): string {
        return `/_auth/api/operator/tenants/${encodeURIComponent(tenantId)}/users`;
    }

    async list(tenantId: string): Promise<TenantUser[]> {
        const res = await authenticatedFetch(this.base(tenantId));
        if (!res.ok) throw new Error(await readError(res, `Failed to load users (${res.status})`));
        return (await res.json()) as TenantUser[];
    }

    async invite(tenantId: string, input: InviteTenantUserInput): Promise<void> {
        const res = await authenticatedFetch(this.base(tenantId), {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to invite user (${res.status})`));
    }

    async setEnabled(tenantId: string, userId: string, enabled: boolean): Promise<void> {
        const res = await authenticatedFetch(`${this.base(tenantId)}/${encodeURIComponent(userId)}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to update user (${res.status})`));
    }

    async remove(tenantId: string, userId: string): Promise<void> {
        const res = await authenticatedFetch(`${this.base(tenantId)}/${encodeURIComponent(userId)}`, { method: 'DELETE' });
        if (!res.ok) throw new Error(await readError(res, `Failed to delete user (${res.status})`));
    }

    /** Re-send a user's setup/verify email (`invite`) or a password-reset email (`reset`). */
    async resendInvite(tenantId: string, userId: string, kind: 'invite' | 'reset'): Promise<{ emailSent: boolean }> {
        const res = await authenticatedFetch(`${this.base(tenantId)}/${encodeURIComponent(userId)}/resend-invite`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind }),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to send email (${res.status})`));
        return (await res.json()) as { emailSent: boolean };
    }
}
